import type { FC } from 'react';

import { useTouring } from 'components/self-tour/hooks/useTouring';

import { BuyDirectContextModal } from './PropertySection/BuyDirectContextModal';
import { FloatingCTA } from './PropertySection/FloatingCTA';
import { SelfTourDialog } from './PropertySection/SelfTourDialog';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';
import type { PropertyDetailsType } from './types';

type FooterCTAProperty = Parameters<typeof useTouring>[0] &
  Pick<
    PropertyDetailsType,
    | 'isFavoriteLoading'
    | 'isLiked'
    | 'onToggleFavorite'
    | 'slug'
    | 'tourBookingListingId'
    | 'street'
    | 'city'
  >;

interface FooterCTAProps {
  property: FooterCTAProperty;
  isVisible?: boolean;
}

export const FooterCTA: FC<FooterCTAProps> = ({ property, isVisible = true }) => {
  const touringInfo = useTouring(property, { placement: 'footer' });

  // Only show FooterCTA for actionable properties
  if (!touringInfo) {
    return null;
  }

  const {
    primaryAction,
    secondaryAction,
    tourKind,
    isDialogOpen,
    closeDialog,
    isSelfTourDialogOpen,
    openSelfTourDialog,
    closeSelfTourDialog,
    isBuyDirectContextOpen,
    closeBuyDirectContext,
    buyDirectContextHref,
  } = touringInfo;

  return (
    <>
      <FloatingCTA
        ctaText={primaryAction.text}
        onClickCta={primaryAction.onClick}
        ctaHref={primaryAction.href}
        ctaAnalyticsName={primaryAction.analyticsName}
        isFavoriteLoading={property.isFavoriteLoading}
        isLiked={property.isLiked}
        onToggleFavorite={property.onToggleFavorite}
        variant={primaryAction.variant}
        isVisible={isVisible}
        disabled={primaryAction.disabled}
        showSecondaryButton={!!secondaryAction}
        secondaryButtonText={secondaryAction?.text}
        secondaryButtonHref={secondaryAction?.href}
        onClickSecondary={secondaryAction?.onClick}
        secondaryAnalyticsName={secondaryAction?.analyticsName}
      />
      {tourKind === 'tourWithAgent' && (
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={closeDialog} />
      )}
      <SelfTourDialog
        isOpen={isSelfTourDialogOpen}
        onClose={closeSelfTourDialog}
        onOpenRequest={openSelfTourDialog}
        placement="footer"
        addressToken={property.addressToken}
        slug={property.slug}
        tourBookingListingId={property.tourBookingListingId}
        addressLine={[property.street, property.city].filter(Boolean).join(', ')}
      />
      <BuyDirectContextModal
        isOpen={isBuyDirectContextOpen}
        onClose={closeBuyDirectContext}
        continueHref={buyDirectContextHref}
      />
    </>
  );
};
